import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { DELAYED_COMMUNICATION_DATE } from '../../../../actions/types'
import * as Constants from '../../../../config/Constants'
import * as actions from '../../../../actions'
import { formatDateForDisplay, notifySuccess } from '../../../shared/Utility'
import { CheckRemainingSubjects, IsLicenseValid } from '../../../shared/auth/license/LicenseChecker'
import getText from '../../../shared/i18n/labels'

// MODAL
import { Modal } from 'react-bootstrap'
import { DelayedCommunicationModal } from '../communications/DelayedCommunicationModal'
import { ImportDataModal } from '../import/NewImportDataModal'
import { UploadReceiptModal } from '../receipts/NewUploadReceiptModal'
import { CoraSubjectsExportModal } from './NewCoraSubjectsExportModal'

// COMPONENTS
import { CoraSubjectList } from './NewCoraSubjectList'
import { PopupError } from '../../../shared/popups/PopupError'
import { PopupDelete } from '../../../shared/popups/PopupDelete'
import { PopupSubjectLicense } from '../../../shared/popups/PopupSubjectLicense'
import { PageSpinner } from '../../../shared/spinner/PageSpinner'
import { LicenseExpiredPopup } from '../../../shared/auth/license/LicenseExpiredPopup'
import ComureiSubjectNotPresentAlert from '../../../shared/popups/ComureiSubjectNotPresentAlert'
import { SubjectTotalAndCallToAction } from '../../../shared/SubjectTotalAndCallToAction'

import { StarTooltip } from '../../../shared/tooltips/Tooltip'
import { BtnFilter } from '../../../shared/BtnFilter'
import { PopupConfirm, titleColors } from '../../../shared/popups/PopupConfirm'
import { PopupSuccess } from '../../../shared/popups/PopupSuccess'

export function CoraSubject() {
  const dispatch = useDispatch()

  const lang = useSelector((state) => state.language.language)
  const labels = getText(lang)

  const [searchObject, setSearchObject] = useState({})
  const [id, setId] = useState(null)
  const [hideSearch, setHideSearch] = useState(true)
  const [subjects, setSubjects] = useState([])
  const [checkedItems, setCheckedItems] = useState([])
  const [checkedSubjects, setCheckedSubjects] = useState([])
  const [customerApplication, setCustomerApplication] = useState('')
  const [subjectSummary, setSubjectSummary] = useState(null)
  const [expired, setExpired] = useState(false)
  const [disableNewSubjectButtons, setDisableNewSubjectButtons] = useState(true)
  const [showDelayedCommunicationModal, setShowDelayedCommunicationModal] = useState(false)
  const [showDelayedCommunicationHint, setShowDelayedCommunicationHint] = useState(false)
  const [loading, setLoading] = useState(false)
  const [licenseExpiration, setLicenseExpiration] = useState()
  const [showSubjectImportModal, setShowSubjectImportModal] = useState(false)
  const [resetForm, setResetForm] = useState(false)
  const [archived, setArchived] = useState(false)

  const auth = useSelector((state) => state.auth)
  const _subjects = useSelector((state) => state.subjects.data)
  const [useHover, setUseHover] = useState(true)

  const isExpired = () => {
    if (auth?.isAuth) {
      return !IsLicenseValid(auth.customer?.customerApplications, Constants.APPLICATION_CORA)
    }
  }

  const setNewCheckedItems = (newCheckedItems) => {
    setCheckedItems(newCheckedItems)
    const newCheckedSubjects = []
    if (newCheckedItems?.length > 0) {
      newCheckedItems.forEach((id) => {
        let subject = checkedSubjects.find((s) => s.id?.toString() === id?.toString())
        if (!subject) subject = subjects.find((s) => s.id?.toString() === id?.toString())
        if (subject) newCheckedSubjects.push(subject)
      })
    }
    setCheckedSubjects(newCheckedSubjects)
  }

  const delayedCommunicationDate = useSelector((state) => state.delayedCommunicationDate)

  useEffect(() => {
    getAllLocations()
    getSubjectSummary()
  }, [])

  useEffect(() => {
    setSubjects(_subjects)
  }, _subjects)

  useEffect(() => {
    if (expired) LicenseExpiredPopup({})
  }, [expired])

  useEffect(() => {
    setExpired(isExpired())
    checkLicense()
  }, [auth])

  const getSubjects = () => {
    actions.searchCoraSubjects(searchObject).then((res) => {
      setSubjects(res?.content || res?.data)
    })
  }

  const checkLicense = () => {
    const application = Constants.APPLICATIONS.find(
      (a) => a.description === Constants.APPLICATION_CORA
    )
    const newCustomerApplication = auth?.user?.customer?.customerApplications?.find(
      (ca) => ca?.application?.id === application.id
    )
    setCustomerApplication(newCustomerApplication)
    setLicenseExpiration(newCustomerApplication?.expiration)
  }

  const getSubjectSummary = (shouldDisplayError) => {
    CheckRemainingSubjects(
      Constants.APPLICATION_CORA,
      shouldDisplayError,
      (subjectSummary) => {
        setDisableNewSubjectButtons(subjectSummary?.available < 1)
        setSubjectSummary(subjectSummary)
        if (subjectSummary?.available < 0) {
          PopupSubjectLicense(
            {
              total: subjectSummary.total,
              used: subjectSummary.used,
              name: Constants.APPLICATION_CORA,
              available: subjectSummary.available
            },
            lang
          )
        }
      },
      labels,
      lang
    )
  }

  const getAllLocations = () => {
    dispatch(actions.getAllLocations())
  }

  const addOrUpdateCoraSubject = (newSubject, previousFiscalCode, searchObject) => {
    newSubject = trimFields(newSubject)
    const promise = newSubject.id
      ? actions.updateCoraSubject(newSubject)
      : actions.addCoraSubject(newSubject)
    promise.then(
      (response) => {
        if (previousFiscalCode !== newSubject.fiscalCode) {
          verifyCoraSubjectOnComurei(newSubject.fiscalCode)
        } else {
          notifySuccess(labels.SUBJECT_SAVED_CORRECTLY)
        }

        setId(response.id)

        getSubjects(searchObject)
        getSubjectSummary(true)
      },
      (errors) => {
        if (errors.status === 511) {
          PopupSubjectLicense(
            {
              total: subjectSummary.total,
              used: subjectSummary.used,
              available: subjectSummary?.available,
              name: Constants.APPLICATION_CORA
            },
            lang
          )
        } else {
          PopupError({
            text: errors?.data?.title || labels.GENERIC_ERROR,
            labels
          })
        }
      }
    )
  }

  const verifyCoraSubjectOnComurei = (fiscalCode, completeName) => {
    actions.verifyCoraSubjectOnComurei(fiscalCode).then(
      (res) => {
        if (res === false) {
          ComureiSubjectNotPresentAlert(
            { fiscalCode: fiscalCode, denomination: completeName },
            lang
          )
        } else {
          notifySuccess(labels.CORRECTLY_SAVED)
        }
      },
      (errors) => {
        console.log(errors)
        notifySuccess(labels.CORRECTLY_SAVED)
      }
    )
  }

  const openPopupDelete = () => {
    PopupDelete({
      operazione: labels.SELECTED_SUBJECTS,
      handleClickConfirm: deleteCoraSubjects,
      text:
        lang === 'it'
          ? `ATTENZIONE: se in futuro dovesse servire ripristinare questo/i soggetto/i, dovrà essere chiesta l'autorizzazione all'ufficio Assistenza.<br/><br/>In alternativa all'eliminazione puoi valutare l'acquisto della FUNZIONE DI ARCHIVIAZIONE SOGGETTI  clicca <a href='https://www.starsoluzioni.it/nuova-funzione-archiviazione-soggetti/'>qui</a> per maggiori dettagli`
          : `WARNING: if you need to restore this/these subject(s) in the future, you will need to request authorization from the Assistance Office.<br/><br/> Alternatively, you can consider purchasing the SUBJECT ARCHIVING function click <a href='https://www.starsoluzioni.it/new-subject-archiving-function/'>here</a> for more details`
    })
  }

  /**
   * effettua l'archiviazione (o disarchiviazione) dei soggetti selezionati
   */
  const archiveCoraSubjects = () => {
    setLoading(true)
    let ids = [...checkedItems]
    actions
      .archiveCoraSubjects(ids)
      .then(
        (res) => {
          PopupSuccess({
            text: `${
              res.filter((el) => {
                return el.actionSuccessful
              }).length
            } soggetto/i archiviati con successo.`
          })
        },
        (err) => PopupError({ text: labels.GENERIC_ERROR })
      )
      .then(() => {
        setLoading(false)
        getSubjects()
        getSubjectSummary(false)
        setCheckedItems([])
        setId()
      })
  }

  const openArchiveSubjectsPopup = () => {
    PopupConfirm({
      titleColor: titleColors.ALERT,
      component: () => labels.ARCHIVE_MULTIPLE_SUBJECTS_MODAL_CONFIRM(checkedItems.length),
      handleClickConfirm: () => {
        archiveCoraSubjects()
      }
    })
  }

  const deleteCoraSubjects = () => {
    setLoading(true)
    let promises = []
    checkedItems.map((subject) => {
      promises.push(actions.deleteCoraSubject(subject))
    })
    Promise.all(promises)
      .then(
        () => {
          setResetForm(!resetForm)
          notifySuccess(labels.REMOVED_SUCCESSFULLY)
        },
        (errors) => {
          PopupError({ text: labels.GENERIC_ERROR })
        }
      )
      .then(() => {
        setLoading(false)
        getSubjects()
        getSubjectSummary(false)
        setCheckedItems([])
      })
  }

  const trimFields = (subject) => {
    if (subject.companyName) {
      //TODO: trimmare lato BE
      subject.companyName = subject.companyName.trim()
    } else if (subject.firstName && subject.lastName) {
      subject.firstName = subject.firstName.trim()
      subject.lastName = subject.lastName.trim()
    }
    return subject
  }

  const onChangeScheduledDate = (date) => {
    dispatch({ type: DELAYED_COMMUNICATION_DATE, delayedCommunicationDate: date })
    setShowDelayedCommunicationModal(false)
  }

  const actionsComponent = () => {
    return (
      <div className="col-12 ps-0">
        <div className="btn-group" role="group" aria-label="action buttons">
          <BtnFilter
            className="ms-1"
            isClose={hideSearch}
            label={labels.SEARCH}
            resetLabel={labels.RESET}
            handlerOnClick={() => {
              setArchived(false)
              setHideSearch(!hideSearch)
            }}
          />
          <button
            type="button"
            disabled={checkedItems.length === 0}
            className={`btn btn-outline-primary  ${checkedItems.length === 0 ? 'disabled' : ''}`}
            onClick={() => openPopupDelete()}>
            <i className="star-icon-trash star-icon me-2" />
            {labels.DELETE}
          </button>
          {!archived && (
            <button
              type="button"
              disabled={checkedItems.length === 0}
              className={`btn btn-outline-primary  ${checkedItems.length === 0 ? 'disabled' : ''}`}
              onClick={() => openArchiveSubjectsPopup()}>
              <i className="star-icon-attachment star-icon me-2" />
              {labels.ARCHIVE}
            </button>
          )}
          <CoraSubjectsExportModal
            disabled={checkedItems.length === 0}
            checkedItems={checkedSubjects}
          />
          <StarTooltip text={labels.IMPORT_SUBJECTS_TO_YOUR_LIST}>
            <button
              type="button"
              className={`btn btn-outline-primary`}
              disabled={disableNewSubjectButtons || expired}
              onClick={() => setShowSubjectImportModal(true)}>
              <i className="star-icon-file-import star-icon me-2" />
              {labels.IMPORT}
            </button>
          </StarTooltip>
          {showSubjectImportModal && (
            <ImportDataModal
              show={showSubjectImportModal}
              onSuccess={() => {
                checkLicense()
                getSubjects()
                setShowSubjectImportModal(false)
              }}
              onClose={() => setShowSubjectImportModal(false)}
            />
          )}
          <UploadReceiptModal disabled={expired} refreshSubjects={getSubjects} />
          {delayedCommunicationDate ? (
            <>
              <button
                type="button"
                className={`btn btn-danger `}
                onClick={() => onChangeScheduledDate(null)}>
                <i className="star-icon-trash star-icon me-2" />
                {labels.SCHEDULED_DELAYED_SENDING_SET_TO}{' '}
                {formatDateForDisplay(delayedCommunicationDate)}
              </button>
              <button
                type="button"
                className={`btn btn-outline-primary`}
                onClick={() => setShowDelayedCommunicationHint(true)}>
                <i className="star-icon-question star-icon" />
              </button>
            </>
          ) : (
            <button
              type="button"
              className={`btn btn-outline-primary`}
              onClick={() => setShowDelayedCommunicationModal(true)}>
              <i className="star-icon-calendar star-icon me-2" />
              {labels.SCHEDULED_DELAYED_SENDING}
            </button>
          )}
          <button
            className={`btn ${archived ? 'tag-orange' : 'tag-outline-orange'} m-0`}
            onClick={() => setArchived(!archived)}>
            {archived ? labels.HIDE_ARCHIVED_SUBJECTS : labels.SHOW_ARCHIVED_SUBJECTS}
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {loading && <PageSpinner />}
      <div className={''}>
        <div className="table-custom">
          <div className="tabs single">
            <div className="d-flex align-items-center justify-content-between">
              <div className="ps-0 d-flex align-items-center">
                <SubjectTotalAndCallToAction
                  total={subjectSummary?.total}
                  remaining={subjectSummary?.available}
                  used={subjectSummary?.used}
                  licenseType={customerApplication?.licenseType}
                  customerApplication={customerApplication}
                  language={lang}
                />
              </div>

              <div className="d-flex text pe-0 align-items-center justify-content-end">
                <div className="col d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-primary mb-2 "
                    disabled={expired || disableNewSubjectButtons}
                    onClick={() => {
                      setResetForm(!resetForm)
                      setUseHover(false)
                    }}>
                    <i className="star-icon-plus star-icon me-2" />
                    {labels.NEW_SUBJECT}
                  </button>
                </div>
              </div>
            </div>

            <CoraSubjectList
              disabled={expired || loading || subjectSummary?.available < 0}
              id={id}
              subjectSummary={subjectSummary}
              disableNewSubjectButtons={disableNewSubjectButtons}
              hideSearch={hideSearch}
              archived={archived}
              resetForm={resetForm}
              subjects={subjects}
              useHover={useHover}
              checkedItems={checkedItems}
              setCheckedItems={setNewCheckedItems}
              setUseHover={setUseHover}
              reloadSubjects={getSubjects}
              setSubjects={setSubjects}
              addOrUpdateCoraSubject={addOrUpdateCoraSubject}
              checkLicense={checkLicense}
              setHideSearch={(val) => setHideSearch(val)}
              actionsComponent={actionsComponent()}
              setSearchObject={setSearchObject}
            />
          </div>
        </div>

        {showDelayedCommunicationModal && (
          <DelayedCommunicationModal
            show={showDelayedCommunicationModal}
            date={delayedCommunicationDate}
            licenseExpiration={licenseExpiration}
            onConfirm={(date) => onChangeScheduledDate(date)}
            onHide={() => setShowDelayedCommunicationModal(false)}
          />
        )}
        {showDelayedCommunicationHint && (
          <Modal
            centered
            backdrop="static"
            aria-labelledby="contained-modal-title-vcenter"
            show={showDelayedCommunicationHint}
            onHide={() => setShowDelayedCommunicationHint(false)}>
            <div className="bar"></div>
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
              <div className="container">
                <p>
                  {labels.SCHEDULED_DELAYED_SENDING_SET_TO}:
                  <strong>{formatDateForDisplay(delayedCommunicationDate)}</strong>
                </p>
                <p>{labels.ALL_AT_THE_END_OF_THE_SESSION_OR_PRESSING_F5_THE_SETTING_IS_DELETED}</p>
                <p>
                  {labels.THE_ACTIVE_SETTING_REMAINS_VALID_FOR_ANY_COMMUNICATIONS_CREATED_WITH_IT}
                </p>
                <p>
                  {
                    labels.TO_CANCEL_THESE_SENDINGS_THE_ROW_MUST_BE_DELETED_FROM_THE_COMMUNICATION_HISTORY
                  }
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className={'rowjustify-content-center'}>
                <button
                  type="button"
                  className={`btn btn-outline-primary`}
                  onClick={() => setShowDelayedCommunicationHint(false)}>
                  {labels.OK_CLOSE}
                </button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </>
  )
}
